import { Fragment } from "react"
import { useDevices } from "@daily-co/daily-react"
import { Settings } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  chooseDevice,
  DEVICE_FALLBACK_NAME_KEYS,
  type DeviceKind,
} from "./devicePreferences"

// What useDevices() returns per device; StatefulDevice isn't exported from
// the package root, so a structural type stands in.
interface DeviceOption {
  device: MediaDeviceInfo
  selected: boolean
}

// The in-call gear (#940): the same three pickers as the pre-join screen,
// switching devices live without leaving the call.
export function DeviceSettingsMenu() {
  const { t } = useTranslation()
  const {
    cameras,
    microphones,
    speakers,
    currentCam,
    currentMic,
    currentSpeaker,
    setCamera,
    setMicrophone,
    setSpeaker,
  } = useDevices()

  const group = (
    kind: DeviceKind,
    label: string,
    list: DeviceOption[],
    current: DeviceOption | undefined,
    apply: (id: string) => Promise<void> | void,
  ) => {
    // Safari lists no audiooutput devices at all, and pre-permission entries
    // can carry empty ids Radix would reject — hide what can't be chosen.
    const options = list.filter((o) => o.device.deviceId !== "")
    if (options.length === 0) return null
    return (
      <Fragment>
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={current?.device.deviceId ?? ""}
          onValueChange={(id) => chooseDevice(kind, id, options.map((o) => o.device), apply)}
        >
          {options.map((o, i) => (
            <DropdownMenuRadioItem key={o.device.deviceId} value={o.device.deviceId}>
              {o.device.label || t(DEVICE_FALLBACK_NAME_KEYS[kind], { number: i + 1 })}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </Fragment>
    )
  }

  const groups = [
    group("camera", t("liveSession.devices.camera"), cameras, currentCam, setCamera),
    group("microphone", t("liveSession.devices.microphone"), microphones, currentMic, setMicrophone),
    group("speaker", t("liveSession.devices.speakers"), speakers, currentSpeaker, setSpeaker),
  ].filter(Boolean)

  if (groups.length === 0) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="secondary"
          size="icon"
          className="rounded-full"
          aria-label={t("liveSession.devices.settings")}
        >
          <Settings className="w-5 h-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="end" className="w-72">
        {groups.map((g, i) => (
          <Fragment key={i}>
            {i > 0 && <DropdownMenuSeparator />}
            {g}
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
